import { inject, Injectable, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

import { SchedulerItemOnMachineCRUD } from '../../../../api/elite/dto/scheduler-item-on-machine.dto';
import { SchedulerItemOnMachineApiAbstract } from '../../../../common/scheduler/api/item-on-machine';
import { SchedulerDragDropApiAbstract } from '../../../../common/scheduler/api/drag-drop';
import { SchedulerWorkOrderOnMachine } from '../../../../common/scheduler/models/wo-on-machine';
import { SchedulerSubWorkOrderOnMachine } from '../../../../common/scheduler/models/sub-wo-on-machine';
import { SchedulerWorkOrderOnMachineApiService } from '../../../../common/scheduler/api/work-order-on-machine.service';
import { SchedulerSubWoOnMachineApiService } from '../../../../common/scheduler/api/sub-wo-on-machine.service';
import { SchedulingApiRoutesDto } from '../../../../api/elite/dto/scheduling.dto';
import { TypeItemOnMachineStopBody } from 'src/app/api/backend/dto/scheduler-item-on-machine.dto';
import { ApiRouteWorkOrderService } from '../../../../api/elite/routes/work-order.service';
import { ApiRouteWorkOrderTypes } from '../../../../api/elite/dto/work-order.dto';

type AddBody = SchedulerItemOnMachineCRUD['add'];
type UpdateBody = SchedulerItemOnMachineCRUD['update'];

type SchedulerItemOnMachine =
  | SchedulerWorkOrderOnMachine
  | SchedulerSubWorkOrderOnMachine;

export const SCHEDULER_ITEM_TYPE = new InjectionToken<
  'work_order' | 'sub_work_order'
>('SchedulerType');

@Injectable({
  providedIn: 'root',
})
export class FilterSchedulerItemOnMachineService
  implements SchedulerItemOnMachineApiAbstract, SchedulerDragDropApiAbstract
{
  private readonly apiWorkOrderRouter = inject(ApiRouteWorkOrderService);
  TYPE: 'work_order' | 'sub_work_order';

  private itemOnMachineService = {
    work_order: inject(SchedulerWorkOrderOnMachineApiService),
    sub_work_order: inject(SchedulerSubWoOnMachineApiService),
  };

  setItemType(type: 'work_order' | 'sub_work_order'): void {
    this.TYPE = type;
  }

  addItem(body: AddBody): Observable<SchedulerItemOnMachine> {
    return this.itemOnMachineService[this.TYPE]?.addItem(body);
  }

  updateItem(body: UpdateBody): Observable<SchedulerItemOnMachine> {
    return this.itemOnMachineService[this.TYPE]?.updateItem(body);
  }

  removeRunning(machineId: number, itemId: number): Observable<any> {
    return this.itemOnMachineService[this.TYPE]?.removeRunning(
      machineId,
      itemId
    );
  }

  removeItem(workOrderId: number, machineId: number): Observable<any> {
    return this.itemOnMachineService[this.TYPE]?.removeItem(
      workOrderId,
      machineId
    );
  }

  /**
   * Required by `SchedulerDragDropApiAbstract`. When the user drags an item
   * out of a machine and back into the waiting list, the parent
   * `SchedulerListDragDropService` re-ranks the item via `updateRank`. This
   * must hit the work-order rank endpoint in production mode and the
   * sub-work-order rank endpoint in packing mode — otherwise dragging a
   * sub-work-order off a machine 404s against the work-order endpoint.
   */
  updateRank(itemId: number, rank: string): Observable<any> {
    return this.itemOnMachineService[this.TYPE]?.updateRank(itemId, rank);
  }

  startItemOnMachine(itemId: number, machineId: number): Observable<any> {
    return this.itemOnMachineService[this.TYPE]?.startItemOnMachine(
      itemId,
      machineId
    );
  }

  stopItemOnMachineV2(
    body: SchedulingApiRoutesDto['registerProgress']['body']
  ): Observable<any> {
    return this.itemOnMachineService[this.TYPE]?.v2StopItemOnMachine(body);
  }
  stopItemOnMachine(body: TypeItemOnMachineStopBody): Observable<any> {
    throw new Error('Method not implemented.');
  }

  getQuantitiesOfWorkOrder(
    transaction: string | number
  ): Observable<ApiRouteWorkOrderTypes['quantities']['result']> {
    return this.apiWorkOrderRouter.getQuantities(transaction);
  }
}
